/* eslint-disable react-refresh/only-export-components */
import { useTranslation } from "react-i18next";
import { PublicManager } from "../homeSlice";
import "../../../i18n/i18n";
import { Option } from "@qivia/ui/src/designSystem/components/Menu/MenuOptions";
import { useCallback, useMemo } from "react";
import styled from "styled-components";
import { DeleteOutlined, Spacer, TextCapitalized } from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { useAppSelector } from "../../../redux/hooks";
import { selectRulesList } from "./rulesSlice";
import { PublicRuleType } from "./rulesAPI";

const StyledLabel = styled.div`
  display: flex;
  align-items: center;
`;

export const useMenuOptionsRules = (props: {
  currentManager: PublicManager | null;
  setRuleToDelete: (ruleToDelete: PublicRuleType | null) => void;
}): Option[] => {
  const { currentManager, setRuleToDelete } = props;
  const { t } = useTranslation();
  const rulesList = useAppSelector(selectRulesList);

  const getRuleToDelete = useCallback(
    (props: { ruleToDeleteUuid: string }) => {
      return rulesList.find((r) => r.uuid === props.ruleToDeleteUuid) ?? null;
    },
    [rulesList],
  );

  const canRuleBeDeleted = useCallback(
    (ruleToDeleteUuid?: string) => {
      if (!ruleToDeleteUuid) {
        return false;
      }
      const ruleFound = getRuleToDelete({ ruleToDeleteUuid }) ?? null;
      return ruleFound && ruleFound.vehicleCount === 0;
    },
    [getRuleToDelete],
  );

  const canManagerDeleteRule = useCallback(() => {
    return currentManager?.role === "OWNER" || currentManager?.role === "ADMIN";
  }, [currentManager?.role]);

  const canDeleteRule = useCallback(
    (props: { ruleToDeleteUuid?: string }) => {
      return canManagerDeleteRule() && canRuleBeDeleted(props.ruleToDeleteUuid);
    },
    [canManagerDeleteRule, canRuleBeDeleted],
  );

  return useMemo<Option[]>(
    () => [
      {
        label: (
          <StyledLabel>
            <DeleteOutlined />
            <Spacer x={0.5} />
            <TextCapitalized>
              {t("rules.management.sidePanel.delete")}
            </TextCapitalized>
          </StyledLabel>
        ),
        color: colors["colors/system/error/error_normal"],
        action: (id) => {
          if (!id || !canDeleteRule({ ruleToDeleteUuid: id })) return;
          setRuleToDelete(getRuleToDelete({ ruleToDeleteUuid: id }));
        },
        disabledHoverTooltip: (id) => {
          if (!id) return null;
          const rule = getRuleToDelete({ ruleToDeleteUuid: id });
          if (rule && rule.vehicleCount > 0 && canManagerDeleteRule()) {
            return `${t("rules.delete.tooltip.content")}`;
          }
          return null;
        },
        isDisabled: (id) => {
          return !id || !canDeleteRule({ ruleToDeleteUuid: id });
        },
      },
    ],
    [t, canDeleteRule, setRuleToDelete, getRuleToDelete, canManagerDeleteRule],
  );
};
